import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRewards, getCurrentMember, isAuthenticated, Reward, Member } from '../services/api';
import { Lock, Sparkles, BadgePercent, Star, Gift, Trophy, Medal, Award, CheckCircle } from 'lucide-react';

const RewardsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'insider_perk' | 'points_reward'>('insider_perk');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const [rewardsData, memberData] = await Promise.all([
        getRewards(),
        getCurrentMember()
      ]);
      setRewards(rewardsData.rewards);
      setMember(memberData);
    } catch (err) {
      console.error('Failed to load rewards:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Access': return Lock;
      case 'Experience': return Sparkles;
      case 'Discount': return BadgePercent;
      case 'Service': return Star;
      default: return Gift;
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Gold': return Trophy;
      case 'Silver': return Medal;
      default: return Award;
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'text-yellow-500';
      case 'Silver': return 'text-gray-400';
      default: return 'text-amber-700';
    }
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'bg-yellow-500 text-yellow-900';
      case 'Silver': return 'bg-gray-400 text-gray-900';
      default: return 'bg-amber-700 text-white';
    }
  };

  const filteredRewards = rewards.filter(r => r.rewardType === activeTab);
  const sortedRewards = activeTab === 'points_reward' 
    ? [...filteredRewards].sort((a, b) => a.pointsCost - b.pointsCost)
    : filteredRewards;

  const lockedPerksCount = rewards.filter(r => r.rewardType === 'insider_perk' && !r.isEligible).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buggy-blue mx-auto mb-4"></div>
          <p className="text-buggy-gray">Loading rewards...</p>
        </div>
      </div>
    );
  }

  const TierIcon = member ? getTierIcon(member.currentTier) : Award;
  const tierColor = member ? getTierColor(member.currentTier) : 'text-amber-700';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40">
        {/* Header */}
        <div className="bg-buggy-blue text-white p-6">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Rewards</h1>
                <p className="text-sm opacity-90">Exclusive perks just for you</p>
              </div>
              {member && (
                <div className="flex flex-col items-end">
                  <div className="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 mb-2 shadow-sm">
                    <TierIcon size={16} strokeWidth={2.5} className={tierColor} />
                    <span className="text-xs font-semibold text-buggy-dark">{member.currentTier} Tier</span>
                  </div>
                  <p className="text-xs opacity-75">Your Points</p>
                  <p className="text-2xl font-bold">{member.currentPoints}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-gray-50 px-4 pt-4 pb-2">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-xl shadow-md p-1.5 flex">
              <button
                onClick={() => setActiveTab('insider_perk')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-sm transition-colors ${
                  activeTab === 'insider_perk'
                    ? 'bg-buggy-blue text-white'
                    : 'text-buggy-gray hover:bg-gray-100'
                }`}
              >
                <Gift size={18} strokeWidth={2} />
                Insider Perks
              </button>
              <button
                onClick={() => setActiveTab('points_reward')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-sm transition-colors ${
                  activeTab === 'points_reward'
                    ? 'bg-buggy-blue text-white'
                    : 'text-buggy-gray hover:bg-gray-100'
                }`}
              >
                <Star size={18} strokeWidth={2} />
                Points Rewards
              </button>
            </div>

            {/* Tab Description */}
            <p className="text-xs text-buggy-gray text-center mt-3">
              {activeTab === 'insider_perk'
                ? 'Free perks included with your tier membership'
                : 'Premium rewards you can purchase with your points'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        {/* Rewards List */}
        <div className="max-w-md mx-auto px-4 pt-2 space-y-4">
        {sortedRewards.map((reward) => {
          const CategoryIcon = getCategoryIcon(reward.rewardCategory || 'Service');
          const RewardTierIcon = getTierIcon(reward.minTierRequired);

          return (
            <button
              key={reward.rewardId}
              onClick={() => navigate(`/rewards/${reward.rewardId}`)}
              className="w-full bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-shadow text-left"
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  reward.isEligible ? 'bg-buggy-blue/10' : 'bg-gray-100'
                }`}>
                  <CategoryIcon 
                    size={28} 
                    strokeWidth={2} 
                    className={reward.isEligible ? 'text-buggy-blue' : 'text-gray-400'} 
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className={`font-bold ${reward.isEligible ? 'text-buggy-dark' : 'text-gray-400'}`}>
                      {reward.rewardName}
                    </h3>
                    
                    {/* Tier Badge - Only for Insider Perks */}
                    {activeTab === 'insider_perk' && (
                      <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0 ${getTierBadgeColor(reward.minTierRequired)}`}>
                        <RewardTierIcon size={12} strokeWidth={2.5} />
                        {reward.minTierRequired}
                      </span>
                    )}
                  </div>

                  {/* Category Label */}
                  <p className="text-xs text-buggy-gray mb-1">{reward.rewardCategory}</p>

                  {/* Description */}
                  <p className={`text-sm mb-3 ${reward.isEligible ? 'text-buggy-gray' : 'text-gray-400'}`}>
                    {reward.rewardDescription}
                  </p>

                  {/* Footer: Cost/Status */}
                  <div className="flex items-center justify-between">
                    {/* Cost */}
                    {activeTab === 'points_reward' && (
                      <span className="text-sm font-bold text-buggy-blue">
                        {reward.pointsCost} pts
                      </span>
                    )}
                    {activeTab === 'insider_perk' && (
                      <span className="text-sm font-medium text-green-600">
                        Free Benefit
                      </span>
                    )}

                    {/* Status Badges */}
                    <div className="flex items-center gap-2">
                      {!reward.isEligible && (
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded-full flex items-center gap-1">
                          <Lock size={12} strokeWidth={2} />
                          Locked
                        </span>
                      )}
                      {reward.isEligible && reward.pointsCost === 0 && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-600 rounded-full flex items-center gap-1">
                          <CheckCircle size={12} strokeWidth={2} />
                          Unlocked
                        </span>
                      )}
                      {reward.isEligible && reward.pointsCost > 0 && !reward.canAfford && (
                        <span className="text-xs px-2 py-1 bg-orange-100 text-orange-600 rounded-full">
                          Need more points
                        </span>
                      )}
                      {reward.isEligible && reward.canAfford && reward.pointsCost > 0 && (
                        <span className="text-xs px-2 py-1 bg-buggy-blue text-white rounded-full font-medium">
                          Redeem Now
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Inventory */}
                  {reward.availableQuantity && reward.remainingQuantity !== null && (
                    <p className="text-xs text-buggy-gray mt-2">
                      {reward.remainingQuantity} of {reward.availableQuantity} available
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}

        {/* Unlock More Perks Card - Only on Insider Perks tab */}
        {activeTab === 'insider_perk' && lockedPerksCount > 0 && member && (
          <div className="bg-gradient-to-r from-buggy-blue to-buggy-blue-light rounded-xl p-5 text-white">
            <h3 className="font-bold mb-2">Unlock {lockedPerksCount} More Perks</h3>
            <p className="text-sm opacity-90 mb-3">
              Reach the next tier to access exclusive benefits
            </p>
            <div className="w-full bg-white/20 rounded-full h-2 mb-2">
              <div
                className="bg-white h-2 rounded-full transition-all duration-500"
                style={{ width: `${Math.min((member.currentPoints / 300) * 100, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs opacity-75">
              {member.currentPoints} / 300 pts to Silver
            </p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default RewardsPage;
